import React, { useState } from "react";
import { useField } from "formik";
import {
  Box,
  Button,
  Flex,
  TextField,
  IconButton,
  Text,
} from "@radix-ui/themes";
import { Plus, X } from "lucide-react";

interface ArrayFieldProps {
  name: string;
  label?: string;
  placeholder?: string;
  description?: string;
  required?: boolean;
  disabled?: boolean;
  emptyStateText?: string;
  itemLabel?: string; // e.g., "URL", "Email", "Tag"
  validation?: (value: string) => boolean;
  formatValue?: (value: string) => string;
}

export default function ArrayField({
  name,
  label,
  placeholder = "Enter value",
  description,
  required = false,
  disabled = false,
  emptyStateText = "No items configured.",
  itemLabel = "item",
  validation,
  formatValue,
}: ArrayFieldProps) {
  const [field, { error, touched }, helpers] = useField<string[]>(name);
  const [newValue, setNewValue] = useState("");

  const values = field.value || [];

  const addValue = () => {
    const trimmedValue = newValue.trim();
    if (!trimmedValue) return;

    // Optional validation
    if (validation && !validation(trimmedValue)) return;

    // Check for duplicates
    if (values.includes(trimmedValue)) return;

    const formattedValue = formatValue
      ? formatValue(trimmedValue)
      : trimmedValue;
    helpers.setValue([...values, formattedValue]);
    setNewValue("");
  };

  const removeValue = (index: number) => {
    const newValues = values.filter((_, i) => i !== index);
    helpers.setValue(newValues);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addValue();
    }
  };

  const hasError = touched && error;

  return (
    <Box>
      {label && (
        <Text
          as="label"
          size="2"
          weight="medium"
          mb="2"
          style={{ display: "block" }}
        >
          {label}
          {required && <Text color="red"> *</Text>}
        </Text>
      )}

      {description && (
        <Text as="div" size="2" color="gray" mb="3">
          {description}
        </Text>
      )}

      {/* Add new value input */}
      <Flex gap="2" mb="3">
        <TextField.Root
          style={{ flex: 1 }}
          placeholder={placeholder}
          value={newValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setNewValue(e.target.value)
          }
          onKeyDown={handleKeyPress}
          disabled={disabled}
        />
        <Button
          type="button"
          variant="surface"
          onClick={addValue}
          disabled={
            !newValue.trim() || values.includes(newValue.trim()) || disabled
          }
        >
          <Plus size={16} />
          Add
        </Button>
      </Flex>

      {/* List of current values */}
      {values.length > 0 && (
        <Flex direction="column" gap="2" mb="2">
          <Text as="div" size="2" weight="medium" color="gray">
            Current {itemLabel}s:
          </Text>
          {values.map((value, index) => (
            <Flex
              key={index}
              align="center"
              justify="between"
              gap="2"
              p="2"
              style={{
                backgroundColor: "var(--gray-a2)",
                borderRadius: "var(--radius-2)",
              }}
            >
              <Text
                as="div"
                size="2"
                style={{
                  fontFamily: "var(--font-mono)",
                  wordBreak: "break-all",
                }}
              >
                {value}
              </Text>
              <IconButton
                type="button"
                variant="ghost"
                color="red"
                size="1"
                onClick={() => removeValue(index)}
                disabled={disabled}
              >
                <X size={14} />
              </IconButton>
            </Flex>
          ))}
        </Flex>
      )}

      {/* Empty state */}
      {values.length === 0 && (
        <Box
          p="3"
          style={{
            backgroundColor: "var(--gray-a2)",
            borderRadius: "var(--radius-2)",
            border: "2px dashed var(--gray-a6)",
          }}
        >
          <Text as="div" size="2" align="center" color="gray">
            {emptyStateText}
          </Text>
        </Box>
      )}

      {/* Error display */}
      {hasError && (
        <Text as="div" size="2" color="red" mt="2">
          {error}
        </Text>
      )}
    </Box>
  );
}
